import JSEncrypt from "jsencrypt";
import cryptoRandomString from "crypto-random-string";
import CryptoJS from "crypto-js";
import { E_ChannelOptions } from "../globalTypes/GlobalTypes";

let store: any = undefined;

export const injectStoreForAxiosUtils = (_store: any) => {
  store = _store;
};

export type T_EncryptionKeys = { iv: string; key: string };

// Cache for encryption keys to avoid repeated Redux store lookups per request
const encryptionKeyCache = new Map<string, T_EncryptionKeys>();

//OSP-7617 helpers start here
export const parseDeep = (input: string) => {
  let result = input;
  while (typeof result === "string") {
    result = JSON.parse(result);
  }
  return result;
};
//OSP-7617 helpers end here

export const getCachedEncryptionKeys = (
  store: any,
  url: string,
): { iv: string; key: string } | null => {
  try {
    const tokenDetails = store.getState()?.CsAppSlice?.tokenDetails;
    const configuration = tokenDetails?.configuration;
    const IV = configuration?.iv || "";
    const secretKey = configuration?.key || "";

    if (!IV || !secretKey) return null;

    const cacheKey = `${IV}:${secretKey}`;

    // Check if keys are already cached - RETRIEVE from cache
    if (encryptionKeyCache.has(cacheKey)) {
      return encryptionKeyCache.get(cacheKey) || null;
    }

    // Cache miss - store new keys for future use
    const keys = { iv: IV, key: secretKey };
    encryptionKeyCache.set(cacheKey, keys);
    return keys;
  } catch (error) {
    console.error("Error getting cached encryption keys:", error);
    return null;
  }
};

// Generates a random IV and its RSA encrypted form for the "statement" header
export const createEncryptedStatement = (jsEncrypt: JSEncrypt) => {
  const iv = cryptoRandomString({ length: 16 });
  const encryptedIv = jsEncrypt.encrypt(iv);
  return { iv, encryptedIv };
};

// RSA encrypts the request payload for the "requesttoken" header (OSP-7617)
export const encryptAccountTakeOverPayload = (
  jsEncrypt: JSEncrypt,
  data: unknown,
) => jsEncrypt.encrypt(JSON.stringify(structuredClone(data)));

export const getLoginSource = (channel: E_ChannelOptions) => {
  if (channel === E_ChannelOptions.ONE_ABC) return "11";
  if (channel === E_ChannelOptions.SUPER_APP) return "25";
  return "26";
};

export const encryptString = (payload: any, IV: string, secretKey: string) => {
  const key = CryptoJS.enc.Utf8.parse(secretKey);
  const salt = CryptoJS.enc.Utf8.parse(IV);
  return CryptoJS.AES.encrypt(payload, key, {
    iv: salt,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
};

export const decryptString = (
  data: string | null,
  IV: string,
  secretKey: string,
) => {
  if (!data) {
    return "";
  }
  const key = CryptoJS.enc.Utf8.parse(secretKey);
  const salt = CryptoJS.enc.Utf8.parse(IV);
  const decrypted = CryptoJS.AES.decrypt(data, key, {
    iv: salt,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

export const getDecryptedSearchParamsValue = (
  searchParamsString: string,
  configurationData?: T_EncryptionKeys,
) => {
  const decodedUrl = decodeURIComponent(searchParamsString);
  const IV = configurationData?.iv || "";
  const key = configurationData?.key || "";

  if (!IV || !key) {
    throw new Error("Invalid decryption configuration");
  }

  const decryptedSearchParams = decryptString(decodedUrl, IV, key);
  if (!decryptedSearchParams) {
    throw new Error("Unable to decrypt search params");
  }

  const pairs = decryptedSearchParams.split("&");
  let tempData: any = {};
  pairs.forEach((pair) => {
    const [key, value] = pair.split("=");
    if (key) {
      tempData[key] = value;
    }
  });
  return tempData;
};

// Decrypts an AES encrypted response (whole body or just the payload) in place
export const performDecryption = (
  response: any,
  IV: string,
  secretKey: string,
  decryptWholeResponse: boolean,
) => {
  try {
    const decryptedResponse = decryptString(
      decryptWholeResponse ? response.data : response.data.payload,
      IV,
      secretKey,
    );
    let transformedResponse = decryptedResponse;
    if (decryptedResponse) {
      transformedResponse = JSON.parse(decryptedResponse);
    }
    if (decryptWholeResponse) {
      response.data = transformedResponse;
    } else {
      response.data.payload = transformedResponse;
    }
  } catch (error) {
    console.error("Error during deferred decryption:", error);
  }
};
